import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";

export interface FinanceMetricCardProps {
  title: string;
  amountCents?: number | null;
  amountFormatted?: string;
  count?: number;
  countLabel?: string;
  description?: string;
  emptyState?: string;
  source?: string | string[];
  icon?: React.ComponentType<{ className?: string }>;
  variant?: "default" | "emerald" | "rose" | "amber" | "blue" | "neutral";
  onAction?: () => void;
  actionLabel?: string;
  subtitle?: string;
}

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

export function formatEuroCents(cents?: number | null): string {
  if (cents === null || cents === undefined) return "—";
  return euro.format(cents / 100);
}

export function FinanceMetricCard({
  title,
  amountCents,
  amountFormatted,
  count,
  countLabel,
  description,
  emptyState,
  source,
  icon: Icon,
  variant = "default",
  onAction,
  actionLabel = "Ver",
  subtitle,
}: FinanceMetricCardProps) {
  const displayAmount = amountFormatted ?? formatEuroCents(amountCents);
  const sourcesList = Array.isArray(source) ? source.join(", ") : source;

  const variantBorderClass = {
    default: "border-border hover:border-border/80",
    emerald: "border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/10",
    rose: "border-rose-200 dark:border-rose-900/50 bg-rose-50/10",
    amber: "border-amber-200 dark:border-amber-900/50 bg-amber-50/10",
    blue: "border-blue-200 dark:border-blue-900/50 bg-blue-50/10",
    neutral: "border-slate-200 dark:border-slate-800 bg-slate-50/10",
  }[variant];

  const amountColorClass = {
    default: "text-foreground",
    emerald: "text-emerald-700 dark:text-emerald-400",
    rose: "text-rose-700 dark:text-rose-400",
    amber: "text-amber-700 dark:text-amber-400",
    blue: "text-blue-700 dark:text-blue-400",
    neutral: "text-foreground",
  }[variant];

  return (
    <Card className={`relative min-h-[148px] flex flex-col justify-between transition-all duration-150 shadow-sm hover:shadow ${variantBorderClass}`}>
      <CardHeader className="space-y-0 pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
            <CardTitle className="text-xs font-medium leading-snug text-muted-foreground uppercase tracking-wide truncate">
              {title}
            </CardTitle>
          </div>
          {count !== undefined && (
            <Badge
              variant={count > 0 ? "default" : "secondary"}
              className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full"
            >
              {count} {countLabel ? countLabel : ""}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2 pb-4 px-4 flex-1 flex flex-col justify-between">
        <div className="space-y-1">
          <div className={`text-2xl font-bold tracking-tight tabular-nums ${amountColorClass}`}>
            {displayAmount}
          </div>
          {subtitle && <p className="text-xs text-muted-foreground font-medium">{subtitle}</p>}
          {(description || emptyState) && (
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {count && count > 0 ? description : emptyState || description}
            </p>
          )}
        </div>

        {(sourcesList || onAction) && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/30 text-[11px]">
            {sourcesList ? (
              <span className="truncate text-muted-foreground/80 font-normal" title={sourcesList}>
                {sourcesList}
              </span>
            ) : <span />}
            {onAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onAction}
                className="h-7 px-2 text-xs font-medium text-ods-navigation hover:text-ods-navigation hover:bg-accent gap-1 shrink-0"
              >
                <span>{actionLabel}</span>
                <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export interface FinanceMetricGridProps {
  children: React.ReactNode;
  maxColumns?: 4 | 3 | 2;
  className?: string;
}

export function FinanceMetricGrid({ children, maxColumns = 4, className = "" }: FinanceMetricGridProps) {
  const colClass = {
    4: "grid gap-3 sm:grid-cols-2 lg:grid-cols-4",
    3: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3",
    2: "grid gap-3 sm:grid-cols-2",
  }[maxColumns];

  return <div className={`${colClass} ${className}`}>{children}</div>;
}
